import { getEchoClient } from '@/realtime/echoClient'

/**
 * Сигнализация звонка поверх presence-канала `lesson.{id}.call`.
 *
 * SDP и ICE-кандидаты идут client events (whisper) — мимо бэкенда: их десятки
 * на установку одного соединения, и хранить их незачем. Reverb ретранслирует
 * такие события только участникам канала (`accept_client_events_from=members`)
 * и сам подмешивает отправителя, поэтому подделать чужой канал нельзя.
 *
 * Жизненный цикл звонка (кто начал, кто вошёл, звонок завершён) идёт **не**
 * через whisper, а через REST + серверное событие `lesson.call.state`:
 * оно переживает перезагрузку страницы и доходит до тех, кто ещё не в канале.
 */

export type CallSignalPayload =
  | { kind: 'offer'; sdp: string }
  | { kind: 'answer'; sdp: string }
  | { kind: 'candidate'; candidate: RTCIceCandidateInit }

export type CallSignalMessage = {
  /** Отправитель. */
  from: number
  /** Адресат: в mesh событие получают все, лишнее отбрасывается по этому полю. */
  to: number
  payload: CallSignalPayload
}

export type CallPresenceUser = { id: number; name: string }

const EVENT_SIGNAL = 'call.signal'
const EVENT_LEAVE = 'call.leave'

export type CallChannelHandlers = {
  onHere?: (users: CallPresenceUser[]) => void
  onJoining?: (user: CallPresenceUser) => void
  onLeaving?: (user: CallPresenceUser) => void
  onSignal?: (message: CallSignalMessage) => void
  /** Участник вышел сам — закрываем соединение, не дожидаясь ICE-таймаута. */
  onPeerLeave?: (payload: { from: number }) => void
  onSubscribed?: () => void
  onError?: (error: unknown) => void
}

export type CallChannel = {
  sendSignal: (message: CallSignalMessage) => void
  sendLeave: (from: number) => void
  leave: () => void
}

function normalizeUsers(raw: unknown[]): CallPresenceUser[] {
  return raw
    .map((u) => {
      const o = u as { id?: unknown; name?: unknown }
      return { id: Number(o.id), name: String(o.name ?? '') }
    })
    .filter((u) => Number.isFinite(u.id))
}

export function joinCallChannel(lessonId: number, handlers: CallChannelHandlers): CallChannel {
  /*
   * Клиент берётся в момент входа в канал, а не через useEchoClient(): это не хук.
   * Смена JWT каналу больше не страшна — Echo при ней не пересоздаётся; а единственное,
   * что его пересоздаёт — смена пользователя (вход, выход, impersonation), при которой
   * текущий звонок всё равно заканчивается вместе с сессией.
   */
  const echo = getEchoClient()
  const channelName = `lesson.${lessonId}.call`

  const channel = echo.join(channelName)

  type ChannelWithLifecycle = typeof channel & {
    subscribed?: (cb: () => void) => typeof channel
    error?: (cb: (err: unknown) => void) => typeof channel
    whisper?: (event: string, data: unknown) => typeof channel
    listenForWhisper?: (event: string, cb: (data: unknown) => void) => typeof channel
  }
  const ch = channel as ChannelWithLifecycle

  if (typeof ch.subscribed === 'function') {
    ch.subscribed(() => handlers.onSubscribed?.())
  } else {
    handlers.onSubscribed?.()
  }

  if (typeof ch.error === 'function') {
    ch.error((err) => handlers.onError?.(err))
  }

  channel
    .here((users: unknown[]) => handlers.onHere?.(normalizeUsers(users)))
    .joining((user: unknown) => {
      const u = normalizeUsers([user])[0]
      if (u) handlers.onJoining?.(u)
    })
    .leaving((user: unknown) => {
      const u = normalizeUsers([user])[0]
      if (u) handlers.onLeaving?.(u)
    })

  ch.listenForWhisper?.(EVENT_SIGNAL, (data: unknown) => {
    const message = data as CallSignalMessage
    if (!message || typeof message.from !== 'number' || typeof message.to !== 'number') return
    handlers.onSignal?.(message)
  })

  ch.listenForWhisper?.(EVENT_LEAVE, (data: unknown) => {
    const payload = data as { from?: unknown }
    if (typeof payload?.from !== 'number') return
    handlers.onPeerLeave?.({ from: payload.from })
  })

  return {
    sendSignal: (message: CallSignalMessage) => {
      ch.whisper?.(EVENT_SIGNAL, message)
    },
    sendLeave: (from: number) => {
      ch.whisper?.(EVENT_LEAVE, { from })
    },
    leave: () => {
      try {
        echo.leave(channelName)
      } catch {
        // Канал мог быть уже закрыт разрывом соединения — это не ошибка.
      }
    },
  }
}
